import { FC } from 'react';

import { CustomTrendChartTooltip } from './CustomTrendChartTooltip';
import { TrendChartSkeleton } from './TrendChartSkeleton';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Skeleton } from '@/components';
import CustomTickWithBackground from '@/components/organisms/EditCell/components/EditCellChartRenderer/components/CustomTickWithBackground';
import { CHARTS_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

import { Card, CardContent } from '../../atoms/Card/Card';

const SMALL_VISIBLE_LINES = 3;
const MEDIUM_VISIBLE_LINES = 5;

type DotProps = {
  index: number;
  cx?: number;
  cy?: number;
  payload: TrendChartDataItem;
};

export type TrendChartDataItem = {
  date: string;
  [key: string]: string | number;
};

export type TrendChartLineItem = {
  key: string;
  color: string;
  name: string;
  type?: string;
};

type Size = '1/3' | '2/3' | '3/3';

type TrendChartProps = {
  title: string;
  size?: Size;
  isLoading?: boolean;
  data: TrendChartDataItem[];
  lines: TrendChartLineItem[];
  isEditCellSkeleton?: boolean;
  valueSuffix?: string;
};

export const TrendChart: FC<TrendChartProps> = ({
  data,
  lines,
  size = '2/3',
  title,
  isLoading,
  isEditCellSkeleton = false,
  valueSuffix = '',
}) => {
  const visibleLines = () => {
    if (size === '1/3') return lines.slice(0, SMALL_VISIBLE_LINES);
    if (size === '2/3') return lines.slice(0, MEDIUM_VISIBLE_LINES);
    return lines;
  };

  const countMore = (size: Size) => {
    const totalLines = lines.length;
    let visibleCount = totalLines;

    if (size === '1/3') {
      visibleCount = SMALL_VISIBLE_LINES;
    } else if (size === '2/3') {
      visibleCount = MEDIUM_VISIBLE_LINES;
    }

    return totalLines - visibleCount;
  };

  const showCountMore =
    (size === '1/3' && lines.length > SMALL_VISIBLE_LINES) ||
    (size === '2/3' && lines.length > MEDIUM_VISIBLE_LINES);

  if (isLoading) {
    return <TrendChartSkeleton />;
  }

  return (
    <Card
      data-testid={CHARTS_TEST_IDS.TREND_LINE_CHART}
      className="p-4 border-custom-warm-grey-150"
    >
      <CardContent>
        <div
          className={cn(
            'mb-6 flex gap-[10px] justify-between',
            size === '1/3' ? 'flex-col' : 'flex-row'
          )}
        >
          <p className="text-body-14-semibold !text-custom-warm-grey-700 shrink-0">{title}</p>
          <div
            className={cn('flex flex-wrap gap-3', size === '1/3' ? 'justify-start' : 'justify-end')}
          >
            {visibleLines().map(({ key, color, name }) => (
              <div className="flex shrink-0 items-center gap-2" key={key}>
                <svg width={12} height={12}>
                  <circle cx="6" cy="6" r="6" fill={color} />
                </svg>
                {isEditCellSkeleton ? (
                  <Skeleton className="w-20 h-4" />
                ) : (
                  <p className="text-body-12-regular !text-custom-warm-grey-700">{name}</p>
                )}
              </div>
            ))}
            {showCountMore && (
              <span className="text-body-12-regular !text-custom-warm-grey-500">
                +{countMore(size)} more
              </span>
            )}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={data}
            margin={{
              top: 10,
              right: 50,
              bottom: 17,
            }}
          >
            <CartesianGrid strokeDasharray="12 12" stroke="#F0EEED" vertical={false} />
            <XAxis
              tick={
                isEditCellSkeleton ? (
                  <CustomTickWithBackground rectHeight={12} rectX={0} rectY={10} />
                ) : (
                  {
                    fontSize: '12',
                    fontWeight: 400,
                    letterSpacing: '-0.01em',
                    color: '#A8A29D',
                  }
                )
              }
              dataKey="date"
              axisLine={false}
              textAnchor="end"
              dy={17}
              tickLine={false}
            />
            <YAxis
              strokeDasharray="3 3"
              ticks={[0, 25, 50, 75, 100]}
              axisLine={false}
              tickLine={false}
              dx={-12}
              tickFormatter={value => `${value}${valueSuffix}`}
              tick={
                isEditCellSkeleton ? (
                  <CustomTickWithBackground rectWidth={24} rectHeight={12} rectX={-36} rectY={-6} />
                ) : (
                  {
                    fontSize: '12',
                    fontWeight: 400,
                    letterSpacing: '-0.01em',
                    color: '#79716B',
                  }
                )
              }
            />
            {!isEditCellSkeleton && (
              <Tooltip content={<CustomTrendChartTooltip valueSuffix={valueSuffix} />} />
            )}
            {lines.map(({ key, color, name, type }) => {
              const strokeStyle = type === 'dashed' ? '16 16' : '0';
              return (
                <Line
                  key={key}
                  type={isEditCellSkeleton ? 'linear' : 'monotone'}
                  dataKey={key}
                  stroke={color}
                  strokeLinecap="round"
                  strokeWidth={4}
                  activeDot={!isEditCellSkeleton}
                  strokeDasharray={strokeStyle}
                  dot={
                    isEditCellSkeleton
                      ? false
                      : ({ index, cx, cy, payload }: DotProps) => {
                          if (cx === undefined || cy === undefined) return <g />;
                          const rawValue = payload[key];
                          const formattedValue = rawValue == null ? '' : String(rawValue);
                          return index === data.length - 1 ? (
                            <>
                              <circle
                                cx={cx}
                                cy={cy}
                                r={4.5}
                                fill={color}
                                stroke="#FFFFFF"
                                strokeWidth={3}
                              />
                              <text
                                className="text-body-12-regular !text-custom-warm-grey-500"
                                textAnchor="start"
                                fill="#79716b"
                                x={cx + 8}
                                y={cy + 4}
                              >
                                {`${formattedValue}${valueSuffix}`}
                              </text>
                            </>
                          ) : (
                            <></>
                          );
                        }
                  }
                  label={false}
                  name={name}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
